import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Shield, Copy, Eye, Users, ChevronRight, Check, X, 
  Lock, Unlock, AlertCircle, Plus, Settings, Target,
  Package, TrendingUp, FileText, Home, UserCog, Pencil,
  Trash2, Save, AlertTriangle
} from 'lucide-react';
import apiClient from '../../api/client';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../ui/alert';

// Permission value display component
const PermissionValue = ({ value }) => {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 text-green-600">
        <Check className="w-4 h-4" />
      </span>
    );
  }
  if (value === false || value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-300">
        <X className="w-4 h-4" />
      </span>
    );
  }
  // Scope-based values (all, team, own, assigned)
  const scopeColors = {
    all: 'bg-blue-100 text-blue-700 border-blue-200',
    team: 'bg-purple-100 text-purple-700 border-purple-200',
    own: 'bg-gray-100 text-gray-600 border-gray-200',
    assigned: 'bg-amber-100 text-amber-700 border-amber-200'
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${scopeColors[value] || scopeColors.own}`}>
      {value}
    </span>
  );
};

// Permission category icons
const getCategoryIcon = (category) => {
  const icons = {
    parts: Package,
    pipeline: TrendingUp,
    goals: Target,
    claims: FileText,
    analytics: Home,
    members: Users,
    settings: Settings,
    teams: UserCog,
    tariffs: AlertCircle
  };
  const Icon = icons[category] || Settings;
  return <Icon className="w-4 h-4" />;
};

// Human-readable permission labels
const getPermissionLabel = (action) => {
  const labels = {
    view: 'View',
    edit: 'Edit',
    create: 'Create',
    delete: 'Delete',
    export: 'Export',
    approve: 'Approve',
    invite: 'Invite',
    remove: 'Remove',
    changeRoles: 'Change Roles',
    viewDashboard: 'View Dashboard',
    viewFinancials: 'View Financials',
    viewTeamMetrics: 'View Team Metrics',
    exportReports: 'Export Reports',
    moveStages: 'Move Stages',
    assign: 'Assign',
    viewCosts: 'View Costs',
    editPricing: 'Edit Pricing',
    read: 'Read',
    update: 'Update'
  };
  return labels[action] || action.replace(/([A-Z])/g, ' $1').trim();
};

// Human-readable category labels
const getCategoryLabel = (category) => {
  const labels = {
    parts: 'Parts & Data',
    pipeline: 'Pipeline',
    goals: 'Goals',
    claims: 'Claims',
    analytics: 'Analytics',
    members: 'Team Management',
    settings: 'Settings',
    teams: 'Teams',
    tariffs: 'Tariffs',
    users: 'Users',
    reports: 'Reports',
    forecasts: 'Forecasts',
    scenarios: 'Scenarios'
  };
  return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

// Role card component
const RoleCard = ({ role, onViewDetails, onClone, onPreview, onEdit, onDelete, isAdmin }) => {
  const getRoleBadgeColor = (name) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 border-purple-200',
      manager: 'bg-blue-100 text-blue-800 border-blue-200',
      analyst: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      viewer: 'bg-gray-100 text-gray-800 border-gray-200',
      member: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[name.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer group"
      data-testid={`role-card-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${role.is_system_role ? 'bg-gray-100' : 'bg-blue-50'}`}>
              {role.is_system_role ? (
                <Lock className="w-5 h-5 text-gray-500" />
              ) : (
                <Unlock className="w-5 h-5 text-blue-500" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {role.name}
                <Badge variant="outline" className={`text-xs ${getRoleBadgeColor(role.name)}`}>
                  {role.is_system_role ? 'System' : 'Custom'}
                </Badge>
              </CardTitle>
              <CardDescription className="mt-1">{role.description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Users className="w-4 h-4" />
            <span>{role.member_count} {role.member_count === 1 ? 'member' : 'members'}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Permission summary */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.keys(role.permissions || {}).slice(0, 5).map((category) => (
            <Badge key={category} variant="secondary" className="text-xs">
              {getCategoryLabel(category)}
            </Badge>
          ))}
          {Object.keys(role.permissions || {}).length > 5 && (
            <Badge variant="secondary" className="text-xs">
              +{Object.keys(role.permissions).length - 5} more
            </Badge>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onViewDetails(role); }}
            data-testid={`view-role-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ChevronRight className="w-4 h-4 mr-1" />
            View Details
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onPreview(role); }}
            data-testid={`preview-role-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </Button>
          {isAdmin && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onClone(role); }}
                data-testid={`clone-role-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Copy className="w-4 h-4 mr-1" />
                Clone
              </Button>
              {!role.is_system_role && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onEdit(role); }}
                    data-testid={`edit-role-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); onDelete(role); }}
                    data-testid={`delete-role-${role.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Permission matrix component
const PermissionMatrix = ({ permissions }) => {
  if (!permissions || Object.keys(permissions).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No permissions defined
      </div>
    );
  }

  // Get all unique actions across all categories
  const allActions = new Set();
  Object.values(permissions).forEach(categoryPerms => {
    Object.keys(categoryPerms).forEach(action => allActions.add(action));
  });
  const actionsList = Array.from(allActions).sort();

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" data-testid="permission-matrix">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-4 py-3 text-left font-medium text-gray-700">Category</th>
            {actionsList.map(action => (
              <th key={action} className="px-3 py-3 text-center font-medium text-gray-700">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger className="cursor-help">
                      {getPermissionLabel(action)}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{action}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(permissions).map(([category, categoryPerms]) => (
            <tr key={category} className="border-b hover:bg-gray-50">
              <td className="px-4 py-3 font-medium">
                <div className="flex items-center gap-2">
                  {getCategoryIcon(category)}
                  {getCategoryLabel(category)}
                </div>
              </td>
              {actionsList.map(action => (
                <td key={action} className="px-3 py-3 text-center">
                  {categoryPerms.hasOwnProperty(action) ? (
                    <PermissionValue value={categoryPerms[action]} />
                  ) : (
                    <span className="text-gray-200">-</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Role detail sheet component
const RoleDetailSheet = ({ open, onClose, role, workspace, onClone, isAdmin }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const loadRoleDetails = useCallback(async () => {
    if (!workspace?.workspace?.id || !role?.id) return;
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/api/v1/workspaces/${workspace.workspace.id}/roles/${role.id}`
      );
      if (response.data.success) {
        setDetails(response.data.data);
      }
    } catch (error) {
      console.error('Error loading role details:', error);
    } finally {
      setLoading(false);
    }
  }, [workspace?.workspace?.id, role?.id]);
  
  useEffect(() => {
    if (open && role && workspace) {
      loadRoleDetails();
    }
  }, [open, role, workspace, loadRoleDetails]);
  
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            {role?.name}
            {role?.is_system_role && (
              <Badge variant="secondary" className="text-xs">System Role</Badge>
            )}
          </SheetTitle>
          <SheetDescription>{role?.description}</SheetDescription>
        </SheetHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : details ? (
          <div className="mt-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{details.member_count}</p>
                    <p className="text-sm text-gray-500">Active Members</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <Shield className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{Object.keys(details.permissions || {}).length}</p>
                    <p className="text-sm text-gray-500">Permission Categories</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Separator />
            
            {/* Tabs for Permissions and Members */}
            <Tabs defaultValue="permissions">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="permissions" data-testid="permissions-tab">
                  <Shield className="w-4 h-4 mr-2" />
                  Permissions
                </TabsTrigger>
                <TabsTrigger value="members" data-testid="members-tab">
                  <Users className="w-4 h-4 mr-2" />
                  Members ({details.members?.length || 0})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="permissions" className="mt-4">
                <ScrollArea className="h-[400px] rounded-md border">
                  <PermissionMatrix permissions={details.permissions} />
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="members" className="mt-4">
                <ScrollArea className="h-[400px]">
                  {details.members?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No members assigned to this role
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {details.members?.map((member) => (
                        <div 
                          key={member.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            member.is_active ? 'bg-white' : 'bg-gray-50 opacity-60'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              member.is_active ? 'bg-blue-100' : 'bg-gray-200'
                            }`}>
                              <span className={`font-medium text-sm ${
                                member.is_active ? 'text-blue-600' : 'text-gray-500'
                              }`}>
                                {member.first_name?.[0]}{member.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">
                                {member.first_name} {member.last_name}
                                {!member.is_active && (
                                  <Badge variant="secondary" className="ml-2 text-xs">Inactive</Badge>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">{member.email}</p>
                            </div>
                          </div>
                          {member.last_active_at && (
                            <span className="text-xs text-gray-400">
                              Last active: {new Date(member.last_active_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
            
            {/* Actions */}
            {isAdmin && (
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => onClone(role)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Clone This Role
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

// Preview access dialog
const PreviewAccessDialog = ({ open, onClose, role, workspace }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const loadPreview = useCallback(async () => {
    if (!workspace?.workspace?.id || !role?.id) return;
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/api/v1/workspaces/${workspace.workspace.id}/roles/${role.id}/preview`
      );
      if (response.data.success) {
        setPreview(response.data.data);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setLoading(false);
    }
  }, [workspace?.workspace?.id, role?.id]);
  
  useEffect(() => {
    if (open && role && workspace) {
      loadPreview();
    }
  }, [open, role, workspace, loadPreview]);

  const getNavIcon = (iconName) => {
    const icons = {
      home: Home,
      package: Package,
      'trending-up': TrendingUp,
      target: Target,
      'file-text': FileText,
      settings: Settings,
      users: Users
    };
    const Icon = icons[iconName] || Settings;
    return <Icon className="w-4 h-4" />;
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Preview Access: {role?.name}
          </DialogTitle>
          <DialogDescription>
            See what users with this role can access in the application
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading preview...</div>
          </div>
        ) : preview ? (
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-6 py-4">
              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-900">Accessible Pages</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {preview.summary.accessible_pages} / {preview.summary.total_pages}
                  </span>
                </div>
              </div>
              
              {/* Navigation Access */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Navigation & Pages
                </h4>
                <div className="space-y-2">
                  {preview.navigation.map((item) => (
                    <div 
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        item.accessible 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-red-50 border-red-200'
                      }`}
                      data-testid={`preview-nav-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {getNavIcon(item.icon)}
                        <span className={item.accessible ? 'text-green-800' : 'text-red-800'}>
                          {item.name}
                        </span>
                      </div>
                      {item.accessible ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              {/* Capabilities */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Capabilities by Category
                </h4>
                <div className="space-y-4">
                  {preview.capabilities.map((cap) => (
                    <div key={cap.category} className="border rounded-lg p-4">
                      <h5 className="font-medium mb-2">{cap.category}</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {cap.actions.map((action) => (
                          <div 
                            key={action.name}
                            className={`flex items-center gap-2 text-sm ${
                              action.allowed ? 'text-green-700' : 'text-gray-400'
                            }`}
                          >
                            {action.allowed ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <X className="w-4 h-4 text-gray-300" />
                            )}
                            {action.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
        ) : null}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Clone role dialog
const CloneRoleDialog = ({ open, onClose, sourceRole, workspace, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (open && sourceRole) {
      setName(`${sourceRole.name} (Copy)`);
      setDescription(`Custom role based on ${sourceRole.name}`);
      setError('');
    }
  }, [open, sourceRole]);
  
  const handleClone = async () => {
    if (!name.trim()) {
      setError('Role name is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.post(
        `/api/v1/workspaces/${workspace.workspace.id}/roles/${sourceRole.id}/clone`,
        null,
        { params: { name: name.trim(), description: description.trim() || undefined } }
      );
      
      if (response.data.success) {
        onSuccess(response.data.data);
        onClose();
      }
    } catch (err) {
      console.error('Error cloning role:', err);
      setError(err.response?.data?.detail || 'Failed to clone role');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Clone Role
          </DialogTitle>
          <DialogDescription>
            Create a new custom role based on &ldquo;{sourceRole?.name}&rdquo;
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter role name"
              data-testid="clone-role-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this role's purpose"
              rows={2}
              data-testid="clone-role-description-input"
            />
          </div>
          
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p className="font-medium mb-1">What happens when you clone:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>A new custom role is created with the same permissions</li>
              <li>You can edit the permissions after creation</li>
              <li>No members are assigned to the new role initially</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleClone} 
            disabled={loading || !name.trim()}
            data-testid="confirm-clone-role-btn"
          >
            {loading ? 'Creating...' : 'Create Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit role dialog component
const EditRoleDialog = ({ open, onClose, role, workspace, onSuccess }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  
  useEffect(() => {
    if (open && role) {
      setName(role.name);
      setDescription(role.description || '');
      setPermissions(JSON.parse(JSON.stringify(role.permissions || {})));
      setError('');
      setActiveCategory(Object.keys(role.permissions || {})[0] || null);
    }
  }, [open, role]);
  
  const handlePermissionChange = (category, action, value) => {
    setPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [action]: value
      }
    }));
  };
  
  const handleScopeChange = (category, action, scope) => {
    setPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [action]: scope
      }
    }));
  };
  
  const handleSave = async () => {
    if (!name.trim()) {
      setError('Role name is required');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.put(
        `/api/v1/workspaces/${workspace.workspace.id}/roles/${role.id}`,
        { 
          name: name.trim(), 
          description: description.trim() || null,
          permissions: permissions
        }
      );
      
      if (response.data.success) {
        onSuccess(response.data.data);
        onClose();
      }
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };
  
  const renderPermissionEditor = (category, categoryPerms) => {
    return (
      <div className="space-y-3">
        {Object.entries(categoryPerms).map(([action, value]) => (
          <div key={action} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
            <div>
              <span className="font-medium text-sm">{getPermissionLabel(action)}</span>
              <p className="text-xs text-gray-500">{action}</p>
            </div>
            <div className="flex items-center gap-2">
              {typeof value === 'boolean' || value === null || value === undefined ? (
                <Switch
                  checked={value === true}
                  onCheckedChange={(checked) => handlePermissionChange(category, action, checked)}
                  data-testid={`perm-${category}-${action}`}
                />
              ) : (
                <Select
                  value={value}
                  onValueChange={(newValue) => handleScopeChange(category, action, newValue)}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="own">Own</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5" />
            Edit Role: {role?.name}
          </DialogTitle>
          <DialogDescription>
            Modify role settings and permissions
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-hidden">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <Tabs defaultValue="basic" className="h-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="permissions">Permissions</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 py-4">
              <div>
                <Label htmlFor="role-name">Role Name *</Label>
                <Input
                  id="role-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter role name"
                  className="mt-1"
                  data-testid="edit-role-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="role-description">Description</Label>
                <Textarea
                  id="role-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this role's purpose"
                  rows={3}
                  className="mt-1"
                  data-testid="edit-role-description-input"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="permissions" className="py-4">
              <div className="flex gap-4 h-[350px]">
                {/* Category selector */}
                <div className="w-48 border-r pr-4">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Categories</p>
                  <div className="space-y-1">
                    {Object.keys(permissions).map((category) => (
                      <button
                        key={category}
                        onClick={() => setActiveCategory(category)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                          activeCategory === category 
                            ? 'bg-blue-50 text-blue-700 font-medium' 
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {getCategoryIcon(category)}
                        {getCategoryLabel(category)}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Permission editor */}
                <ScrollArea className="flex-1">
                  {activeCategory && permissions[activeCategory] && (
                    <div>
                      <h4 className="font-medium mb-4 flex items-center gap-2">
                        {getCategoryIcon(activeCategory)}
                        {getCategoryLabel(activeCategory)} Permissions
                      </h4>
                      {renderPermissionEditor(activeCategory, permissions[activeCategory])}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        <DialogFooter className="gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading || !name.trim()}
            data-testid="save-role-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete role dialog component
const DeleteRoleDialog = ({ open, onClose, role, roles, workspace, onSuccess }) => {
  const [reassignToRoleId, setReassignToRoleId] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState('');
  
  // Filter out the role being deleted from reassignment options
  const availableRoles = roles.filter(r => r.id !== role?.id);
  
  useEffect(() => {
    if (open && role && workspace) {
      loadMembers();
      // Default to Viewer role if available
      const viewerRole = availableRoles.find(r => r.name.toLowerCase() === 'viewer');
      setReassignToRoleId(viewerRole?.id || availableRoles[0]?.id || '');
      setError('');
    }
  }, [open, role]);
  
  const loadMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await apiClient.get(
        `/api/v1/workspaces/${workspace.workspace.id}/roles/${role.id}/members`
      );
      if (response.data.success) {
        setMembers(response.data.data);
      }
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };
  
  const handleDelete = async () => {
    if (!reassignToRoleId) {
      setError('Please select a role to reassign members to');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.delete(
        `/api/v1/workspaces/${workspace.workspace.id}/roles/${role.id}`,
        { params: { reassign_to_role_id: reassignToRoleId } }
      );
      
      if (response.data.success) {
        onSuccess(response.data);
        onClose();
      }
    } catch (err) {
      console.error('Error deleting role:', err);
      setError(err.response?.data?.detail || 'Failed to delete role');
    } finally {
      setLoading(false);
    }
  };
  
  const targetRole = availableRoles.find(r => r.id === reassignToRoleId);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Delete Role: {role?.name}
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. All members will be reassigned.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* Warning banner */}
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-800">Warning</AlertTitle>
            <AlertDescription className="text-amber-700">
              {members.length > 0 ? (
                <>This role has <strong>{members.length} member{members.length !== 1 ? 's' : ''}</strong> who will be moved to another role.</>
              ) : (
                <>This role has no members assigned.</>
              )}
            </AlertDescription>
          </Alert>
          
          {/* Member list */}
          {members.length > 0 && (
            <div>
              <Label className="mb-2 block">Affected Members</Label>
              <div className="border rounded-lg max-h-32 overflow-y-auto">
                {loadingMembers ? (
                  <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : (
                  <div className="divide-y">
                    {members.map((member) => (
                      <div key={member.id} className="px-3 py-2 text-sm flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                        <span>{member.first_name} {member.last_name}</span>
                        <span className="text-gray-400">({member.email})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Role selector */}
          <div>
            <Label htmlFor="reassign-role" className="mb-2 block">
              Move all members to: *
            </Label>
            <Select
              value={reassignToRoleId}
              onValueChange={setReassignToRoleId}
            >
              <SelectTrigger id="reassign-role" data-testid="reassign-role-select">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} ({r.member_count} members)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {targetRole && (
              <p className="text-xs text-gray-500 mt-1">
                Members will be moved to &ldquo;{targetRole.name}&rdquo;
              </p>
            )}
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={handleDelete} 
            disabled={loading || !reassignToRoleId}
            data-testid="confirm-delete-role-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {loading ? 'Deleting...' : `Delete & Move ${members.length} Member${members.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to sort roles
const sortRoles = (rolesList) => {
  if (!rolesList) return [];
  return [...rolesList].sort((a, b) => {
    if (a.is_system_role !== b.is_system_role) {
      return a.is_system_role ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
};

// Main RolesTab component
const RolesTab = ({ roles: initialRoles, workspace, onRefresh }) => {
  // Sort roles during initialization, not in useEffect
  const [roles, setRoles] = useState(() => sortRoles(initialRoles));
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Check if current user is admin
  const isAdmin = workspace?.role?.role_name?.toLowerCase() === 'admin' ||
                  workspace?.role?.permissions?.members?.changeRoles;
  
  // Update roles when initialRoles prop changes (using useMemo pattern)
  const sortedRoles = useMemo(() => sortRoles(initialRoles), [initialRoles]);
  
  // Sync state when prop changes
  useEffect(() => {
    setRoles(sortedRoles);
  }, [sortedRoles]);
  
  const handleViewDetails = (role) => {
    setSelectedRole(role);
    setShowDetailSheet(true);
  };
  
  const handlePreview = (role) => {
    setSelectedRole(role);
    setShowPreviewDialog(true);
  };
  
  const handleClone = (role) => {
    setSelectedRole(role);
    setShowCloneDialog(true);
  };
  
  const handleEdit = (role) => {
    setSelectedRole(role);
    setShowEditDialog(true);
  };
  
  const handleDelete = (role) => {
    setSelectedRole(role);
    setShowDeleteDialog(true);
  };
  
  const handleCloneSuccess = (newRole) => {
    setRoles(prev => [...prev, newRole].sort((a, b) => {
      if (a.is_system_role !== b.is_system_role) {
        return a.is_system_role ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    }));
    onRefresh?.();
  };
  
  const handleEditSuccess = (updatedRole) => {
    setRoles(prev => prev.map(r => 
      r.id === updatedRole.id ? { ...r, ...updatedRole } : r
    ));
    onRefresh?.();
  };
  
  const handleDeleteSuccess = () => {
    setRoles(prev => prev.filter(r => r.id !== selectedRole?.id));
    onRefresh?.();
  };
  
  // Separate system and custom roles
  const systemRoles = roles.filter(r => r.is_system_role);
  const customRoles = roles.filter(r => !r.is_system_role);

  return (
    <div data-testid="roles-tab">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-semibold">Roles & Permissions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage role definitions and view permission settings
          </p>
        </div>
        {isAdmin && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Use first system role as template
                    const template = systemRoles.find(r => r.name.toLowerCase() === 'viewer') || systemRoles[0];
                    if (template) {
                      setSelectedRole(template);
                      setShowCloneDialog(true);
                    }
                  }}
                  data-testid="create-role-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Custom Role
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Create a new role by cloning an existing one</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      {/* System Roles Section */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4" />
          System Roles
          <span className="text-gray-400 font-normal normal-case">({systemRoles.length})</span>
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          {systemRoles.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              onViewDetails={handleViewDetails}
              onClone={handleClone}
              onPreview={handlePreview}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </div>
      
      {/* Custom Roles Section */}
      {customRoles.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Unlock className="w-4 h-4" />
            Custom Roles
            <span className="text-gray-400 font-normal normal-case">({customRoles.length})</span>
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {customRoles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                onViewDetails={handleViewDetails}
                onClone={handleClone}
                onPreview={handlePreview}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isAdmin={isAdmin}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Empty state for custom roles */}
      {customRoles.length === 0 && isAdmin && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
          <Unlock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="font-medium text-gray-600 mb-2">No Custom Roles Yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Create custom roles by cloning existing system roles and modifying their permissions.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              const template = systemRoles.find(r => r.name.toLowerCase() === 'viewer') || systemRoles[0];
              if (template) {
                setSelectedRole(template);
                setShowCloneDialog(true);
              }
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Custom Role
          </Button>
        </div>
      )}
      
      {/* Role Detail Sheet */}
      <RoleDetailSheet
        open={showDetailSheet}
        onClose={() => {
          setShowDetailSheet(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        workspace={workspace}
        onClone={handleClone}
        isAdmin={isAdmin}
      />
      
      {/* Preview Access Dialog */}
      <PreviewAccessDialog
        open={showPreviewDialog}
        onClose={() => {
          setShowPreviewDialog(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        workspace={workspace}
      />
      
      {/* Clone Role Dialog */}
      <CloneRoleDialog
        open={showCloneDialog}
        onClose={() => {
          setShowCloneDialog(false);
          setSelectedRole(null);
        }}
        sourceRole={selectedRole}
        workspace={workspace}
        onSuccess={handleCloneSuccess}
      />
      
      {/* Edit Role Dialog */}
      <EditRoleDialog
        open={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        workspace={workspace}
        onSuccess={handleEditSuccess}
      />
      
      {/* Delete Role Dialog */}
      <DeleteRoleDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedRole(null);
        }}
        role={selectedRole}
        roles={roles}
        workspace={workspace}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default RolesTab;
